import { UtilService } from 'src/app/core/services/util/util.service';
import { Injectable, Injector, Input, OnDestroy, inject } from '@angular/core';
import { LocationStrategy, PathLocationStrategy } from '@angular/common';
import { Router, Event, NavigationError } from '@angular/router';
import { Subscription } from 'rxjs';
import { AppGlobalsModule } from 'src/app/app-globals.module';
import { environment } from 'src/environments/environment';
import { I18nService } from '../../services/i18n/i18n.service';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { UrlAddressService } from '../../services/url-address/url-address.service';
import { CookieService } from 'ngx-cookie-service';
import { BrowserPlatformService } from '../../services/browser-platform/browser-platform.service';
import { ERROR_PAGE_ROUTES, isErrorPageUrl } from '../error-page-routes';
@Injectable({
    providedIn: 'root',
})
export class ErrorsService implements OnDestroy {
    private _injector = inject(Injector);
    private _router = inject(Router);
    private _i18n = inject(I18nService);
    private _utils = inject(UtilService);
    private _urlAddressService = inject(UrlAddressService, { optional: true })!;
    private nr = inject<any>('NEWRELIC' as any, { optional: true })!;
    private cookieService = inject(CookieService);
    private _browser = inject(BrowserPlatformService);

    @Input() error: any = null;

    newrelic: any;
    private _subscriptions: Subscription[] = [];
    private _rateLimitNavigation: Promise<boolean> | null = null;

    constructor() {
        const nr = this.nr;

        this.newrelic = nr;
        // Subscribe to the NavigationError
        this._subscriptions.push(
            this._router.events.subscribe((event: Event) => {
                if (event instanceof NavigationError) {
                    // The error page is lazy, so a failed navigation TO it (stale chunk after a
                    // hashed deploy, or offline) must not trigger another one - that feeds back
                    // into itself forever.
                    if (isErrorPageUrl(event.url)) {
                        this.log(event.error);
                        return;
                    }

                    if (event.error instanceof HttpErrorResponse) {
                        void this.handleHttpError(event.error);
                    } else {
                        this.log(event.error);
                        void this.navigateToError();
                    }
                }
            }),
        );
    }

    log(error: any) {
        if (error?.status === HttpStatusCode.TooManyRequests) {
            return error;
        }

        // Log the error to the console
        // console.error(error);

        if (typeof error === 'string' || error instanceof String) {
            error = this.addContextInfo(error);
        }

        const errorToSend = error;
        const errorMessage = this._getErrorMessage(errorToSend);

        // Send error to server
        if (this.newrelic && environment.production) {
            try {
                //TODO - add transaction name to error notice - not work yet?
                if (this._router.routerState.root.routeConfig) {
                    this.newrelic.setTransactionName(this._router.routerState.root.routeConfig.path);
                } else if (this._router.routerState.snapshot.root.routeConfig) {
                    this.newrelic.setTransactionName(this._router.routerState.snapshot.root.routeConfig.path);
                } else {
                    this.newrelic.setTransactionName('URL:' + this._router.routerState.snapshot.url);
                }

                const attributes = {
                    URL: this._router.routerState.snapshot.url || 'Url not found',
                    Status: error?.status || 'No status',
                    Language: this._i18n.getLang(),
                };

                // // Error code 0 unknown means that a request got canceled which is the simpom of the problem and not the root cause
                // // It can be due to a timeout, CORs or network error or Universal restart
                // if (AppGlobalsModule.isServer && error.message.includes('0 Unknown Error')) {
                //     console.error(errorToSend);
                //     return;
                // } else {
                //     this.newrelic.noticeError(errorToSend, attributes);
                // }
                this.newrelic.noticeError(errorToSend, attributes);
            } catch (e) {
                if (AppGlobalsModule.isServer) {
                    console.error('problem to notice error to new relic (from Application Error Service: ' + (e as any).message);
                }
            }
        }
        let logerror = false;
        try {
            // Safe ip or logerror url parameter are enough to show error in enduser's console
            if (this.cookieService.check('IpIsGood') || this._urlAddressService.getParam('logerror')) {
                logerror = true;
            }
        } catch (error) {}

        //log to console in development or universal SSR
        if (!environment.production || AppGlobalsModule.isServer || this._utils.isStaging() || logerror) {
            // Disable this error becouse that users isn't exist in serve
            if (errorMessage.includes('user is not defined')) {
                return;
            }
            console.error(errorToSend);
        }

        // Return Observablenode
        if (!AppGlobalsModule.isServer && (!environment.production || this._utils.isStaging())) {
            if (!this._isKnownError(errorMessage)) {
                let baseErrorText = this._browser.document.body.getAttribute('jserror') ?? '';
                if (baseErrorText) {
                    baseErrorText += '##';
                }
                this._browser.document.body.setAttribute('jserror', baseErrorText + ' error from angular ' + errorMessage);
            }
        }
        return errorToSend;
    }

    public handleHttpError(error: HttpErrorResponse): Promise<boolean> {
        const errorMessage = this._getErrorMessage(error).toLowerCase();

        if (error.status === HttpStatusCode.TooManyRequests) {
            return this.navigateToTooManyRequests();
        }

        if ((error.status >= 400 && error.status < 500) || errorMessage.includes('404 not found')) {
            return this.navigateToNotFound();
        }

        this.log(error);
        return this.navigateToError();
    }

    public navigateToNotFound(): Promise<boolean> {
        return this._router.navigate([`/${ERROR_PAGE_ROUTES.notFound}`], { skipLocationChange: true });
    }

    public navigateToError(): Promise<boolean> {
        return this._router.navigate([`/${ERROR_PAGE_ROUTES.error}`], { skipLocationChange: true });
    }

    public navigateToTooManyRequests(): Promise<boolean> {
        const route = `/${ERROR_PAGE_ROUTES.tooManyRequests}`;

        if (this._router.url === route) {
            return Promise.resolve(true);
        }

        if (!this._rateLimitNavigation) {
            const navigation = this._router.navigate([route], { skipLocationChange: true });
            this._rateLimitNavigation = navigation;
            navigation.then(
                () => {
                    if (this._rateLimitNavigation === navigation) {
                        this._rateLimitNavigation = null;
                    }
                },
                () => {
                    if (this._rateLimitNavigation === navigation) {
                        this._rateLimitNavigation = null;
                    }
                },
            );
        }

        return this._rateLimitNavigation;
    }

    private _isKnownError(msg: string): boolean {
        return !!['Info is not defined', 'warehouse'].some((str) => msg.includes(str));
    }

    private _getErrorMessage(error: any): string {
        if (typeof error?.message === 'string') {
            return error.message;
        }

        if (typeof error === 'string') {
            return error;
        }

        if (error == null) {
            return '';
        }

        try {
            return String(error);
        } catch {
            return '';
        }
    }

    // async notfoundError(error): Promise<any> {
    //     this.error = error;
    //     this.log(error);
    //     return error;
    // }

    addContextInfo(error: any) {
        // You can include context details here (usually coming from other services: UserService...)
        const name = error.name || error;
        const appId = 'Midrash';
        const user = 'UserTzadik';
        const time = new Date().getTime();
        const id = `${appId}-${user}-${time}`;
        const location = this._injector.get(LocationStrategy);
        const url = location instanceof PathLocationStrategy ? location.path() : '';
        const status = error.status || null;
        const message = error.message || error.toString();

        //TODO:Get Stack
        //const stack = error instanceof HttpErrorResponse ? null : StackTraceParser.parse(error);
        const stack = null;

        //const errorWithContext = { name, appId, user, time, id, url, status, message, stack };

        const errorWithContext = {
            Name: name,
            AppId: appId,
            Time: time,
            Url: url,
            Status: status,
            message: message,
        };
        return errorWithContext;
    }

    ngOnDestroy(): void {
        this._utils.unsubscribe(this._subscriptions);
    }
}
